import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
    Box,
    VStack,
    HStack,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Button,
    Divider,
    Icon,
    Flex,
    useColorModeValue,
    Tag
} from '@chakra-ui/react';
import { LuArrowLeft, LuDownload, LuPrinter } from 'react-icons/lu';
import Header from '../../../components/Header';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchWrapper } from '../../../helpers';
import { useReactToPrint } from 'react-to-print';

const BillingDetails = () => {
    // Color mode values
    const headerBg = useColorModeValue('gray.50', 'gray.700');
    const { id } = useParams();
    const [invoiceData, setInvoiceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const printRef = useRef(null);
    const navigate = useNavigate()

    // Correct react-to-print implementation
    const handlePrint = useReactToPrint({
        contentRef: printRef, // Use contentRef instead of content for newer versions
        documentTitle: `Invoice-${invoiceData?.invoiceNumber || 'Unknown'}`,
        pageStyle: `
            @page {
                size: A4;
                margin: 1in;
            }
            @media print {
                body {
                    -webkit-print-color-adjust: exact;
                    color-adjust: exact;
                    print-color-adjust: exact;
                    margin: 0;
                    padding: 20px;
                }
                .no-print {
                    display: none !important;
                }
                .screen-only {
                    display: none !important;
                }
                .print-table {
                    display: table !important;
                    width: 100% !important;
                    border-collapse: collapse !important;
                    margin: 10px 0 !important;
                }
                .print-table th,
                .print-table td {
                    border: 1px solid #e2e8f0 !important;
                    padding: 8px !important;
                    text-align: left !important;
                }
                .print-table th {
                    background-color: #f7fafc !important;
                    font-weight: bold !important;
                }
                .print-card {
                    display: block !important;
                    border: 1px solid #e2e8f0 !important;
                    border-radius: 8px !important;
                    margin-bottom: 16px !important;
                    padding: 16px !important;
                    background-color: white !important;
                }
                .print-flex {
                    display: flex !important;
                    justify-content: space-between !important;
                    align-items: center !important;
                    margin-bottom: 10px !important;
                }
                .print-badge {
                    background-color: #38a169 !important;
                    color: white !important;
                    padding: 6px 12px !important;
                    border-radius: 4px !important;
                    font-size: 14px !important;
                    font-weight: bold !important;
                }
                .print-section {
                    page-break-inside: avoid;
                }
                .text-blue { color: #3182ce !important; }
                .text-gray { color: #718096 !important; }
                .font-bold { font-weight: bold !important; }
                .font-semibold { font-weight: 600 !important; }
                .font-medium { font-weight: 500 !important; }
                .text-xl { font-size: 1.25rem !important; }
                .text-lg { font-size: 1.125rem !important; }
                .text-3xl { font-size: 1.875rem !important; }
                .mb-2 { margin-bottom: 0.5rem !important; }
                .mb-4 { margin-bottom: 1rem !important; }
                .mb-6 { margin-bottom: 1.5rem !important; }
                .p-4 { padding: 1rem !important; }
                .text-right { text-align: right !important; }
                .divider-print {
                    border-top: 1px solid #e2e8f0 !important;
                    margin: 16px 0 !important;
                }
            }
        `,
        onBeforeGetContent: () => {
            console.log('Preparing to print...');
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve();
                }, 100);
            });
        },
        onAfterPrint: () => {
            console.log('Print completed');
        }
    });

    useEffect(() => {
        const fetchInvoiceDetail = async () => {
            try {
                const url = `${process.env.REACT_APP_API_URL}/invoice/detail/${id}`;
                const res = await fetchWrapper.get(url);
                setInvoiceData(res);
            } catch (err) {
                console.error('Failed to load invoice details', err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchInvoiceDetail();
        }
    }, [id]);

    const formatCurrency = (amount, currency = 'USD') => {
        if (currency === 'PKR') {
            return `Rs ${amount.toLocaleString()}`;
        }
        return `$${amount.toLocaleString()}`;
    };

    const getServiceIcon = (ServiceIcon) => {
        return <Icon as={ServiceIcon} boxSize={5} color="blue.500" />;
    };

    const renderServiceBreakdown = (service) => {
        if (!service.channelDetail || service.channelDetail.length === 0) {
            return <Text fontSize="sm" color="gray.500">No detailed breakdown available.</Text>;
        }

        return (
            <Table variant="simple" size="sm" bg="white">
                <Thead bg={headerBg}>
                    <Tr>
                        <Th>Template Type</Th>
                        <Th>Country</Th>
                        <Th>Count</Th>
                        <Th>Rate ({service.currency})</Th>
                        <Th>Amount</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {service.channelDetail.map((item, index) => (
                        <Tr key={index}>
                            <Td>
                                <span className="badge-print">
                                    {item.templateType}
                                </span>
                                <Tag
                                    colorScheme={item.templateType === 'Marketing' ? 'blue' : 'gray'}
                                    variant="solid"
                                    fontSize="xs"
                                    className="no-print"
                                >
                                    {item.templateType}
                                </Tag>
                            </Td>
                            <Td>{item.country}</Td>
                            <Td>{item.count}</Td>
                            <Td>{item.rate.toFixed(4)}</Td>
                            <Td fontWeight="medium">
                                {item.currency === 'PKR'
                                    ? `Rs ${item.amount.toFixed(2)}`
                                    : `${item.amount.toFixed(4)}`
                                }
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        );
    };

    if (loading) {
        return <Box py={20} textAlign="center"><Text>Loading Invoice Details...</Text></Box>;
    }

    if (!invoiceData) {
        return <Box py={20} textAlign="center"><Text color="red.500">Invoice not found.</Text></Box>;
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Pending':
                return <Tag bg={"yellow"}>{status}</Tag>;
            case 'Paid':
                return <Tag bg={"#2563eb"} color={"white"}>{status}</Tag>;
            default:
                return null;
        }
    };

    const handleBackNavigate = () => {
        navigate("/billings")
    }
    return (
        <>
            {/* Header - Will not be printed */}
            <div className="no-print">
                <Header />
            </div>

            <BoxWrapper>
                        <VStack spacing={6} align="stretch">
                            {/* Header with actions - Will not be printed */}
                            <Flex align="center" justify="space-between" className="no-print">
                                <Button
                                    leftIcon={<Icon as={LuArrowLeft} />}
                                    variant="ghost"
                                    size="sm"
                                    color="gray.600"
                                    onClick={handleBackNavigate}
                                >
                                    Back to Billing
                                </Button>
                                <HStack spacing={3}>
                                    <Button
                                        leftIcon={<Icon as={LuDownload} />}
                                        variant="outline"
                                        size="sm"
                                        onClick={handlePrint}

                                    >
                                        Download PDF
                                    </Button>
                                    <Button
                                        leftIcon={<Icon as={LuPrinter} />}
                                        colorScheme="blue"
                                        size="sm"
                                        onClick={handlePrint}
                                        isDisabled={!invoiceData}
                                    >
                                        Print Invoice
                                    </Button>
                                </HStack>
                            </Flex>

                            {/* Invoice Content - This will be printed */}
                            <div ref={printRef} className="print-section">
                                {/* Invoice Header */}
                                <Flex justify="space-between" align="start" mb={6}>
                                    <Box>
                                        <Text fontSize="3xl" fontWeight="bold" color="blue.600" mb={2}>
                                            INVOICE
                                        </Text>
                                        <Text color="gray.600" fontSize="sm">
                                            {invoiceData?.companyName}
                                        </Text>
                                    </Box>
                                    <Box textAlign="right">
                                        <Text fontSize="xl" fontWeight="bold" mb={1}>
                                            {invoiceData?.invoiceNumber}
                                        </Text>
                                        <Text fontSize="sm" color="gray.600" mb={1}>
                                            Generated: {invoiceData?.Date}
                                        </Text>
                                        <Text fontSize="sm" color="gray.600">
                                            Due: {invoiceData?.dueDate}
                                        </Text>
                                    </Box>
                                </Flex>

                                {/* Bill To and Billing Period */}
                                <Flex justify="space-between" align="start" mb={6}>
                                    <Box>
                                        <Text fontWeight="semibold" color="gray.700" mb={2}>
                                            Bill To:
                                        </Text>
                                        <Text fontSize="lg" fontWeight="medium">
                                            {invoiceData?.to}
                                        </Text>
                                    </Box>
                                    <Box textAlign="right">
                                        <Text fontWeight="semibold" color="gray.700" mb={2}>
                                            Billing Period:
                                        </Text>
                                        <Text fontSize="lg" fontWeight="medium">
                                            {invoiceData?.billingdata}
                                        </Text>
                                    </Box>
                                </Flex>

                                <Divider my={6} />

                                {/* Service Usage Details */}
                                <Box mb={6}>
                                    <Text fontSize="xl" fontWeight="semibold" color="gray.800" mb={4}>
                                        Service Usage Details
                                    </Text>

                                    <VStack spacing={6} align="stretch">
                                        {invoiceData?.channelList?.map((service) => (
                                            <div key={service.id}>
                                                {/* Screen version - Chakra UI */}
                                                <Box
                                                    border="1px solid #0000001a"
                                                    borderRadius="10px"
                                                    bg="gray.50"
                                                    p={6}
                                                    className="screen-only"
                                                >
                                                    <Flex align="center" justify="space-between" mb={4}>
                                                        <HStack spacing={3}>
                                                            {getServiceIcon(service?.icon)}
                                                            <Box>
                                                                <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                                                                    {service?.name}
                                                                </Text>
                                                                <Text fontSize="sm" color="gray.600">
                                                                    {service?.totalCount}
                                                                </Text>
                                                            </Box>
                                                        </HStack>
                                                        <Box textAlign="right">
                                                            <Tag
                                                                colorScheme="green"
                                                                fontSize="md"
                                                                px={3}
                                                                py={1}
                                                                fontWeight="bold"
                                                            >
                                                                {service?.currency} {formatCurrency(service?.totalAmount, service?.currency)}
                                                            </Tag>
                                                        </Box>
                                                    </Flex>
                                                    <Box overflowX="auto">
                                                        {renderServiceBreakdown(service)}
                                                    </Box>
                                                </Box>

                                                {/* Print version - Plain HTML */}
                                                <div className="print-card" style={{ display: 'none' }}>
                                                    <div className="print-flex">
                                                        <div>
                                                            <div style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '4px' }}>
                                                                {service?.name}
                                                            </div>
                                                            <div style={{ fontSize: '0.875rem', color: '#718096' }}>
                                                                {service?.totalCount}
                                                            </div>
                                                        </div>
                                                        <div className="print-badge">
                                                            {service?.currency} {formatCurrency(service?.totalAmount, service?.currency)}
                                                        </div>
                                                    </div>
                                                    {renderServiceBreakdown(service)}
                                                </div>
                                            </div>
                                        ))}
                                    </VStack>
                                </Box>

                                <Divider my={6} />

                                {/* Currency Summary */}
                                <VStack spacing={4} align="stretch" mb={6}>
                                    {invoiceData?.currencySummary?.map((item) => (
                                        <Box
                                            border="1px solid #0000001a"
                                            borderRadius="10px"
                                            bg={item?.color}
                                            p={4}
                                            key={item?.amount}
                                            className="chakra-card"
                                        >
                                            <Flex justifyContent={"space-between"}>
                                                <Text fontSize="lg" fontWeight="medium" color="gray.900">
                                                    {item?.symbol} {item?.currency} Services SubTotal
                                                </Text>
                                                <Text fontSize="lg" fontWeight="medium" color="gray.900">
                                                    {item?.symbol}.{item?.amount}
                                                </Text>
                                            </Flex>
                                        </Box>
                                    ))}
                                </VStack>

                                <Divider my={6} />

                                {/* Exchange Data */}
                                <VStack spacing={4} align="stretch" mb={6}>
                                    {invoiceData?.exchangeData?.map((item) => (
                                        <Box
                                            border="1px solid #0000001a"
                                            borderRadius="10px"
                                            bg={"#fffbeb"}
                                            p={4}
                                            key={item?.amount}
                                            className="chakra-card"
                                        >
                                            <Flex justifyContent={"space-between"} alignItems={"center"}>
                                                <Text fontSize="lg" fontWeight="medium" color="gray.900">
                                                    {item?.desc}
                                                </Text>
                                                <Box>
                                                    <Text fontSize="xs" fontWeight="medium" color="gray.400" align={"end"}>
                                                        ${item?.amount} * {item?.rate}
                                                    </Text>
                                                    <Text fontSize="lg" fontWeight="medium" color="gray.900" align={"end"}>
                                                        Rs {Number(item?.exchangeAmount).toFixed(2)}
                                                    </Text>
                                                </Box>
                                            </Flex>
                                        </Box>
                                    ))}
                                </VStack>

                                <Divider my={6} />

                                {/* Total Amount Due */}
                                <Box
                                    border="1px solid #0000001a"
                                    borderRadius="10px"
                                    bg={"#2563eb0d"}
                                    p={4}
                                    className="chakra-card"
                                    mb={4}
                                >
                                    <Flex justifyContent={"space-between"} alignItems={"center"}>
                                        <Text fontSize="lg" fontWeight="medium" color="gray.900">
                                            Total Amount Due
                                        </Text>
                                        <Box>
                                            <Text fontSize="lg" fontWeight="bold" color="#2563eb" align={"end"}>
                                                Rs {Number(invoiceData?.due_amount).toFixed(2)}
                                            </Text>
                                        </Box>
                                    </Flex>
                                </Box>

                                {/* Payment Status */}
                                <Box
                                    border="1px solid #0000001a"
                                    borderRadius="10px"
                                    bg={"#2563eb0d"}
                                    p={4}
                                    className="chakra-card"
                                >
                                    <Flex justifyContent={"space-between"} alignItems={"center"}>
                                        <Text fontSize="lg" fontWeight="medium" color="gray.900">
                                            Payment Status
                                        </Text>
                                        <Box>
                                            <Text fontSize="lg" fontWeight="bold" color="#2563eb" align={"end"}>
                                                {getStatusIcon(invoiceData?.status)}
                                            </Text>
                                        </Box>
                                    </Flex>
                                </Box>
                            </div>
                        </VStack>
            </BoxWrapper>
        </>
    );
};

export default BillingDetails;